import { Fragment, useState, useRef, useEffect } from "react";
import { RefreshCw, Terminal, ChevronLeft, ChevronRight } from "lucide-react";
import { WorkflowStatus } from "@/types/breeze/Workflow";
import { formatDate } from "@/utils/date";
import SplitView from "@/components/ui/SplitView";
import { useQuery } from "@tanstack/react-query";
import { workflowService } from "@/services/breeze/WorkflowService";
import { LoadingScreen } from "@/components/ui/LoadingScreen";
import Surface from "@/components/ui/Surface";
import {
  Badge,
  Button,
  Box,
  Flex,
  Text,
  Heading,
  ScrollArea,
  Tabs,
} from "@radix-ui/themes";
import { Editor } from "@monaco-editor/react";
import { useTheme } from "@/providers/ThemeProvider";

interface WorkflowJobHistoryProps {
  workflowId: number;
}

export default function WorkflowJobHistory({
  workflowId,
}: WorkflowJobHistoryProps) {
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const { isDarkMode } = useTheme();
  const isRunningRef = useRef(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const {
    data: jobsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["workflow-jobs", workflowId, page, pageSize],
    queryFn: () =>
      workflowService.getWorkflowJobHistory(workflowId, page, pageSize),
    staleTime: 0,
    refetchInterval: () => (isRunningRef.current ? 2000 : false),
  });

  const { data: jobData, isLoading: isJobLoading } = useQuery({
    queryKey: ["workflow-job", workflowId, selectedJobId],
    queryFn: () =>
      workflowService.getWorkflowJobHistoryById(workflowId, selectedJobId!),
    enabled: !!selectedJobId,
    staleTime: 0,
    refetchInterval: () => (isRunningRef.current ? 2000 : false),
  });

  const jobs = jobsData?.items || [];
  const totalItems = jobsData?.total_items || 0;
  const totalPages = jobsData?.total_pages || 0;

  const selectedJobFromList = jobs.find((j) => j.id === selectedJobId);
  const isRunning =
    selectedJobId !== null &&
    (selectedJobFromList?.status === WorkflowStatus.Running ||
      jobData?.status === WorkflowStatus.Running);
  isRunningRef.current = isRunning;

  const totalLogsLength = jobData?.steps?.reduce((acc, step) => acc + (step.log?.length || 0), 0) || 0;
  const stepsCount = jobData?.steps?.length || 0;

  useEffect(() => {
    if (jobData?.status === WorkflowStatus.Running) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [stepsCount, totalLogsLength, jobData?.status]);
  const hasNextPage = jobsData?.has_next_page || false;
  const hasPreviousPage = jobsData?.has_previous_page || false;

  if (isLoading) {
    return <LoadingScreen fullPage={false} />;
  }

  if (!jobs || jobs.length === 0) {
    return (
      <Flex align="center" justify="center" p="4">
        <Text color="gray">No job history found for this workflow.</Text>
      </Flex>
    );
  }

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      setPage((prev) => prev - 1);
      setSelectedJobId(null);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      setPage((prev) => prev + 1);
      setSelectedJobId(null);
    }
  };

  // Calculate the job number based on total items and pagination
  const getJobNumber = (index: number) => {
    return totalItems - ((page - 1) * pageSize + index);
  };

  return (
    <Box height="100%" style={{ display: "flex", flexDirection: "column" }}>
      <SplitView>
        <SplitView.Left>
          <Box
            height="100%"
            style={{
              display: "flex",
              flexDirection: "column",
              background: "var(--color-panel-solid)",
              borderRadius: "var(--radius-3)",
            }}
          >
            <Box p="4" style={{ borderBottom: "1px solid var(--gray-a5)" }}>
              <Flex justify="between" align="center" mb="3">
                <Heading size="4">Job History</Heading>
                <Button variant="outline" size="1" onClick={() => refetch()}>
                  <RefreshCw size={16} />
                </Button>
              </Flex>
              <Flex justify="between" align="center">
                <Text size="1" color="gray">
                  Showing {jobs.length > 0 ? (page - 1) * pageSize + 1 : 0} -{" "}
                  {(page - 1) * pageSize + jobs.length} of {totalItems} jobs
                </Text>
                <Flex gap="2">
                  <Button
                    variant="outline"
                    size="1"
                    onClick={handlePreviousPage}
                    disabled={!hasPreviousPage}
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <Text
                    size="1"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: "60px",
                      justifyContent: "center",
                    }}
                  >
                    {page} / {totalPages}
                  </Text>
                  <Button
                    variant="outline"
                    size="1"
                    onClick={handleNextPage}
                    disabled={!hasNextPage}
                  >
                    <ChevronRight size={16} />
                  </Button>
                </Flex>
              </Flex>
            </Box>

            <ScrollArea style={{ flex: 1 }}>
              <Box p="4">
                <Flex direction="column" gap="2">
                  {jobs.map((job, index) => (
                    <Surface
                      key={job.id}
                      onClick={() => setSelectedJobId(job.id)}
                      title={`Job #${getJobNumber(index)}`}
                      body={formatDate(job.created_at)}
                      additionalTitleContent={
                        <Badge
                          color={
                            job.status === WorkflowStatus.Success
                              ? "green"
                              : job.status === WorkflowStatus.Failed
                                ? "red"
                                : job.status === WorkflowStatus.Running
                                  ? "yellow"
                                  : "gray"
                          }
                        >
                          {job.status}
                        </Badge>
                      }
                    />
                  ))}
                </Flex>
              </Box>
            </ScrollArea>
          </Box>
        </SplitView.Left>

        <SplitView.Right>
          <Box
            height="100%"
            style={{
              display: "flex",
              flexDirection: "column",
              background: "var(--color-panel-solid)",
              borderRadius: "var(--radius-3)",
            }}
          >
            {selectedJobId ? (
              isJobLoading ? (
                <Flex height="100%" align="center" justify="center" pt="10">
                  <LoadingScreen fullPage={false} />
                </Flex>
              ) : jobData ? (
                <Box
                  height="100%"
                  style={{ display: "flex", flexDirection: "column" }}
                >
                  <Box
                    p="4"
                    style={{ borderBottom: "1px solid var(--gray-a5)" }}
                  >
                    <Flex justify="between" align="center">
                      <Heading size="4">
                        Job #
                        {getJobNumber(
                          jobs.findIndex((j) => j.id === selectedJobId),
                        )}
                      </Heading>
                      <Flex align="center" gap="2">
                        {jobData.status === WorkflowStatus.Running && (
                          <Flex align="center" gap="1">
                            <RefreshCw size={12} className="animate-spin" />
                            <Text size="1" color="gray">
                              Auto-refreshing...
                            </Text>
                          </Flex>
                        )}
                        <Badge
                          color={
                            jobData.status === WorkflowStatus.Success
                              ? "green"
                              : jobData.status === WorkflowStatus.Failed
                                ? "red"
                                : jobData.status === WorkflowStatus.Running
                                  ? "yellow"
                                  : "gray"
                          }
                        >
                          {jobData.status}
                        </Badge>
                      </Flex>
                    </Flex>
                  </Box>

                  <ScrollArea style={{ flex: 1 }}>
                    <Box p="4">
                      {jobData.error_message && (
                        <Box
                          mb="4"
                          p="3"
                          style={{
                            backgroundColor: "var(--red-a3)",
                            border: "1px solid var(--red-a6)",
                            borderRadius: "var(--radius-3)",
                          }}
                        >
                          <Text
                            weight="medium"
                            size="2"
                            color="red"
                            mb="1"
                            as="div"
                          >
                            Error
                          </Text>
                          <Text size="2" color="red">
                            {jobData.error_message}
                          </Text>
                        </Box>
                      )}

                      <Box mb="4">
                        <Text weight="medium" mb="2" as="div">
                          Payload
                        </Text>
                        <Editor
                          height="150px"
                          defaultLanguage="json"
                          language="json"
                          value={JSON.stringify(
                            JSON.parse(jobData.payload),
                            null,
                            2,
                          )}
                          theme={isDarkMode ? "vs-dark" : "light"}
                          options={{
                            readOnly: true,
                            minimap: { enabled: false },
                          }}
                        />
                      </Box>

                      <Box style={{ display: "flex", flexDirection: "column" }}>
                        <Flex justify="between" align="center" mb="2">
                          <Text weight="medium">Execution Steps</Text>
                          <Button
                            variant="outline"
                            size="1"
                            onClick={() => {
                              const logContent = jobData.steps
                                .map(
                                  (step) =>
                                    `[${step.workflow_step.key}]\n${
                                      step.log || ""
                                    }`,
                                )
                                .join("\n\n");
                              const blob = new Blob([logContent], {
                                type: "text/plain",
                              });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement("a");
                              a.href = url;
                              a.download = `workflow-job-${jobData.id}-logs.txt`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            }}
                          >
                            <Terminal size={16} />
                            Download Logs
                          </Button>
                        </Flex>

                        <Box style={{ flex: 1 }}>
                          <Flex direction="column" gap="4">
                            {jobData.steps?.length === 0 && (
                              <Text color="gray">
                                No steps executed for this job.
                              </Text>
                            )}

                            {jobData.steps?.map((step) => (
                              <Box
                                key={step.id}
                                style={{
                                  border: "1px solid var(--gray-a5)",
                                  borderRadius: "var(--radius-3)",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  p="3"
                                  style={{
                                    borderBottom: "1px solid var(--gray-a5)",
                                  }}
                                >
                                  <Flex justify="between" align="center">
                                    <Text size="2" weight="medium">
                                      {step.workflow_step.name}
                                    </Text>
                                    <Badge
                                      color={step.success ? "green" : "red"}
                                    >
                                      {step.success ? "Success" : "Failed"}
                                    </Badge>
                                  </Flex>
                                </Box>

                                <Tabs.Root defaultValue="logs">
                                  <Box
                                    style={{
                                      borderBottom: "1px solid var(--gray-a5)",
                                    }}
                                  >
                                    <Tabs.List>
                                      <Tabs.Trigger value="logs">
                                        Logs
                                      </Tabs.Trigger>
                                      <Tabs.Trigger value="data">
                                        Data
                                      </Tabs.Trigger>
                                    </Tabs.List>
                                  </Box>

                                  <Box p="3">
                                    <Tabs.Content value="logs">
                                      {step.error_message && (
                                        <Box
                                          mb="2"
                                          p="2"
                                          style={{
                                            backgroundColor: "var(--red-a3)",
                                            border: "1px solid var(--red-a6)",
                                            borderRadius: "var(--radius-2)",
                                          }}
                                        >
                                          <Text size="1" color="red">
                                            {step.error_message}
                                          </Text>
                                        </Box>
                                      )}
                                      <ScrollArea
                                        style={{
                                          maxHeight: "200px",
                                        }}
                                      >
                                        <Box
                                          style={{
                                            whiteSpace: "pre-wrap",
                                            wordBreak: "break-word",
                                            fontFamily: "var(--font-mono)",
                                            fontSize: "var(--font-size-1)",
                                          }}
                                        >
                                          {step.log
                                            ?.split("\n")
                                            .map((line, i) => (
                                              <Fragment key={i}>
                                                {line}
                                                {i <
                                                  step.log!.split("\n").length -
                                                    1 && <br />}
                                              </Fragment>
                                            ))}
                                        </Box>
                                      </ScrollArea>
                                    </Tabs.Content>

                                    <Tabs.Content value="data">
                                      {step.data_json ? (
                                        <Editor
                                          height="300px"
                                          defaultLanguage="json"
                                          language="json"
                                          value={JSON.stringify(
                                            JSON.parse(step.data_json),
                                            null,
                                            2,
                                          )}
                                          theme={
                                            isDarkMode ? "vs-dark" : "light"
                                          }
                                          options={{
                                            readOnly: true,
                                            minimap: { enabled: false },
                                          }}
                                        />
                                      ) : (
                                        <Text size="2" color="gray">
                                          No data available
                                        </Text>
                                      )}
                                    </Tabs.Content>
                                  </Box>
                                </Tabs.Root>
                              </Box>
                            ))}
                          </Flex>
                        </Box>
                        <div ref={bottomRef} />
                      </Box>
                    </Box>
                  </ScrollArea>
                </Box>
              ) : (
                <Flex height="100%" align="center" justify="center" pt="10">
                  <Text color="gray">Failed to load job details</Text>
                </Flex>
              )
            ) : (
              <Flex height="100%" align="center" justify="center" pt="10">
                <Text color="gray">Select a job to view details</Text>
              </Flex>
            )}
          </Box>
        </SplitView.Right>
      </SplitView>
    </Box>
  );
}
